'use client';

import { useMemo } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { EMAIL_REGEX, NAME_REGEX, URL_REGEX } from '@/lib/validation';

import { ClearButton, FormFieldError, SuccessState } from './shared';
import { useFormState } from './use-form-state';

type FieldErrors = Partial<Record<'name' | 'email' | 'website', string>>;

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
  source?: string;
}

const EMPTY_VALUES = { name: '', email: '', website: '' };

function validate(data: { name: string; email: string; website: string }): FieldErrors {
  const errors: FieldErrors = {};

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  if (data.name.trim() && !NAME_REGEX.test(data.name.trim())) {
    errors.name = 'Please enter a valid name (letters, spaces, hyphens, apostrophes)';
  }

  if (!data.website.trim()) {
    errors.website = 'Website is required';
  } else if (!URL_REGEX.test(data.website.trim())) {
    errors.website = 'Please enter a valid website URL';
  }

  return errors;
}

export function WaitlistModal({ open, onClose, source }: WaitlistModalProps) {
  const formConfig = useMemo(
    () => ({
      initialValues: EMPTY_VALUES,
      validate,
      endpoint: '/api/waitlist',
    }),
    [],
  );

  const { formState, errors, values, handleChange, clearField, handleSubmit } = useFormState(
    formConfig,
    open,
    source,
  );

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence mode="wait">
          {formState === 'success' ? (
            <SuccessState
              title="You're on the list!"
              subtitle="We'll be in touch soon with early access details."
              onClose={onClose}
            />
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <DialogHeader className="shrink-0">
                <DialogTitle className="text-2xl">Join the Waitlist</DialogTitle>
                <DialogDescription>Get early access to the AI Commerce platform.</DialogDescription>
              </DialogHeader>

              <form
                onSubmit={handleSubmit}
                noValidate
                className="mt-4 flex min-h-0 flex-1 flex-col"
              >
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                  <div>
                    <label htmlFor="waitlist-name" className="mb-1.5 block text-sm font-medium">
                      Name <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="waitlist-name"
                        name="name"
                        placeholder="Your name"
                        value={values.name}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'waitlist-name-error' : undefined}
                        onChange={e => handleChange('name', e.target.value)}
                        className={values.name ? 'pr-8' : ''}
                      />
                      {values.name && <ClearButton onClick={() => clearField('name')} />}
                    </div>
                    <FormFieldError id="waitlist-name-error" error={errors.name} />
                  </div>

                  <div>
                    <label htmlFor="waitlist-email" className="mb-1.5 block text-sm font-medium">
                      Email <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="waitlist-email"
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        required
                        value={values.email}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'waitlist-email-error' : undefined}
                        onChange={e => handleChange('email', e.target.value)}
                        className={values.email ? 'pr-8' : ''}
                      />
                      {values.email && <ClearButton onClick={() => clearField('email')} />}
                    </div>
                    <FormFieldError id="waitlist-email-error" error={errors.email} />
                  </div>

                  <div>
                    <label htmlFor="waitlist-website" className="mb-1.5 block text-sm font-medium">
                      Website <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="waitlist-website"
                        name="website"
                        type="url"
                        placeholder="yourcompany.com"
                        required
                        value={values.website}
                        aria-invalid={!!errors.website}
                        aria-describedby={errors.website ? 'waitlist-website-error' : undefined}
                        onChange={e => handleChange('website', e.target.value)}
                        className={values.website ? 'pr-8' : ''}
                      />
                      {values.website && <ClearButton onClick={() => clearField('website')} />}
                    </div>
                    <FormFieldError id="waitlist-website-error" error={errors.website} />
                  </div>

                  {formState === 'error' && (
                    <p className="text-sm text-destructive" role="alert">
                      Something went wrong. Please try again.
                    </p>
                  )}
                </div>

                <div className="shrink-0 pt-4">
                  <Button type="submit" className="w-full" disabled={formState === 'submitting'}>
                    {formState === 'submitting' ? 'Joining...' : 'Join Waitlist'}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
